/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.I18N.NEW_LANGUAGE — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/I18nNewLanguagePage.tsx. The desktop
 * screen is an add-new-language WIZARD: it upserts csm_language_master, copies data
 * translations from the home locale (csm_i18n_copy_data_strings) and optionally
 * machine-translates every screen prompt. That provisioning flow is a desktop
 * workflow and stays there.
 *
 * On mobile this is a purpose-built, READ-ONLY roster of the configured locales —
 * the same csm_language_master the desktop reads (§16: never the desktop wizard
 * re-hosted in a phone viewport). KPI tiles summarise the locale set (total /
 * active / inactive); each row is one locale with its flag, human name resolved
 * via localeName() (§17.B4 — never a bare code alone) and a Y/N active status pill.
 * Reads only via apiDb; no writes here, so nothing to route through the Common
 * Routine — adding a language stays on the desktop admin.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Star, Info } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDate } from '../../lib/format_date';
import { localeName, localeFlag } from '../../i18n/locale_display';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface MobileLocaleRow {
  language_id: number;
  internal_code: string;
  is_active: 'Y' | 'N';
  is_default: 'Y' | 'N';
  created_at: string | null;
  updated_at: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';

export function MobileI18nNewLanguagePage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDate = useFormatDate();
  const [locales, setLocales] = useState<MobileLocaleRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileLocaleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: lErr } = await apiDb
        .from('csm_language_master')
        .select('language_id, internal_code, is_active, is_default, created_at, updated_at')
        .order('internal_code');
      if (lErr) throw lErr;
      const rows = ((data ?? []) as MobileLocaleRow[]).map((r) => ({
        language_id: r.language_id,
        internal_code: r.internal_code,
        is_active: r.is_active,
        is_default: r.is_default,
        created_at: r.created_at,
        updated_at: r.updated_at,
      }));
      setLocales(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (names follow it).
  useEffect(() => { void reload(); }, [activeLocale]);

  // Human name for a locale, resolved into the active UI language (§17.B4).
  const nameOf = (code: string) => localeName(code, activeLocale);

  const kpis = useMemo(() => {
    const active = locales.filter((l) => l.is_active === 'Y').length;
    return { total: locales.length, active, inactive: locales.length - active };
  }, [locales]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return locales.filter((l) => {
      if (statusFacet === 'active' && l.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && l.is_active !== 'N') return false;
      if (!q) return true;
      return l.internal_code.toLowerCase().includes(q) || nameOf(l.internal_code).toLowerCase().includes(q);
    });
    // nameOf depends on activeLocale, already a dep via the effect-driven list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locales, filter, statusFacet, activeLocale]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.i18n_new.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const isCurrent = (l: MobileLocaleRow) => l.internal_code === activeLocale;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.i18n_new.title', 'Add Language')}
        description={t('mobile.i18n_new.description', 'Read-only view of configured locales; adding one is a desktop task.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <span className="m-kpi-l">{t('mobile.i18n_new.kpi_total', 'Languages')}</span>
          <span className="m-kpi-v">{kpis.total}</span>
        </div>
        <div className="m-kpi">
          <span className="m-kpi-l">{t('common.boolean.active', 'Active')}</span>
          <span className="m-kpi-v">{kpis.active}</span>
        </div>
        <div className="m-kpi">
          <span className="m-kpi-l">{t('common.boolean.inactive', 'Inactive')}</span>
          <span className="m-kpi-v">{kpis.inactive}</span>
        </div>
      </div>

      <div className="m-card">
        <div className="m-empty" style={{ justifyContent: 'flex-start', gap: '0.5rem' }}>
          <Info size={16} />
          {t('mobile.i18n_new.desktop_hint', 'Adding a language is a desktop task. This is a read-only view of configured locales.')}
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.i18n_new.search', 'Search languages')}
          aria-label={t('mobile.i18n_new.search', 'Search languages')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.i18n_new.none', 'No languages match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((l) => (
            <MobileCardRow
              key={l.language_id}
              lead={localeFlag(l.internal_code)}
              leadTone={isCurrent(l) ? 'blue' : 'slate'}
              title={nameOf(l.internal_code)}
              subtitle={
                l.internal_code
                + (l.is_default === 'Y' ? ' · ' + t('mobile.i18n_new.default', 'Default') : '')
                + (isCurrent(l) ? ' · ' + t('mobile.i18n_new.current', 'Current') : '')
              }
              trailing={
                <MobileStatusPill tone={l.is_active === 'Y' ? 'ok' : 'no'}>
                  {l.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(l)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.i18n_new.detail', 'Language detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av" aria-hidden>{localeFlag(selected.internal_code)}</span>
              <span>
                <div className="m-nm">{nameOf(selected.internal_code)}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.i18n_new.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.i18n_new.default', 'Default')}
                value={
                  <MobileStatusPill tone={selected.is_default === 'Y' ? 'ok' : 'no'}>
                    {selected.is_default === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.i18n_new.code', 'Code')} value={selected.internal_code} />
              <MobileDetailRow label={t('mobile.i18n_new.added', 'Added')} value={formatDate(selected.created_at) || '—'} />
              <MobileDetailRow label={t('mobile.i18n_new.updated', 'Updated')} value={formatDate(selected.updated_at) || '—'} />
              {isCurrent(selected) && (
                <MobileDetailRow
                  label={t('mobile.i18n_new.current', 'Current')}
                  value={<><Star size={14} /> {t('mobile.i18n_new.current_hint', 'Active UI language')}</>}
                />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
